import React, { useState } from 'react'
import Button from 'components/button'
import Modal from 'components/modal'
import Tooltip from 'components/tooltip'
import { BLUE_BUTTON } from 'components/save-buttons'

// The parts each hub settings section is built from, in the manner of shadcn/ui's settings cards:
// related settings grouped in a card with a title and a line saying what's in it, one setting per row.
// A row either stacks its label, control and explanation (a text field, a dropdown), or sets its label
// and explanation beside the control (a switch, a pair of buttons).

/**
 * The sunken canvas a settings section's cards sit on. On a phone (the settings page under 36rem wide) its
 * side padding goes, so the cards run edge to edge for the most room to read.
 */
export const SETTINGS_CANVAS = 'bg-surface-sunken px-6 py-5 @max-xl:px-0 @max-xl:py-3'

/**
 * A card of settings. The danger card is marked by its red border alone; its header reads as any other.
 * `aside` sits at the right of the header: a preview of what the card's settings change. `footer` is a
 * shaded band under the settings, apart from them, for an action on the card as a whole; `footerTone="blue"`
 * tints it light blue, for an invitation rather than a setting (as Upgrade). It clips to its corners without
 * scrolling (overflow: clip), so a table's header in it can stick to the page as it scrolls.
 */
export const SettingsCard: React.FC<{
  title: string
  description?: string
  danger?: boolean
  aside?: React.ReactNode
  footer?: React.ReactNode
  footerTone?: 'sunken' | 'blue'
  children: React.ReactNode
}> = ({ title, description, danger, aside, footer, footerTone = 'sunken', children }) => {
  const headingId = `settings-card-${title.toLowerCase().replace(/\s+/g, '-')}`
  return (
    <section
      // On a phone, edge to edge: no side borders or rounded corners, only the lines above and below it.
      className={`rounded-lg border bg-surface overflow-clip @max-xl:rounded-none @max-xl:border-x-0 ${danger ? 'border-red/40' : 'border-divider'}`}
      aria-labelledby={headingId}
    >
      <header className={`flex items-center gap-4 px-4 py-3 border-b ${danger ? 'border-red/20' : 'border-divider'}`}>
        <div className="flex-1 min-w-0">
          <h2 id={headingId} className="text-[14px] font-semibold text-body">
            {title}
          </h2>
          {/* Kept to a readable line length, however wide the card grows. */}
          {description && <p className="mt-0.5 max-w-[640px] text-pretty text-[13px] text-muted">{description}</p>}
        </div>
        {aside && <div className="shrink-0">{aside}</div>}
      </header>
      {/* Between the rows, lines inset from the card's edges. */}
      <div className={`divide-y ${danger ? 'divide-red/20' : 'divide-divider'}`}>{children}</div>
      {footer && (
        <footer
          className={`flex items-center gap-4 border-t px-4 py-3 text-[13px] ${
            footerTone === 'blue' ? 'border-blue/20 bg-blue/5' : 'border-divider bg-surface-sunken'
          }`}
        >
          {footer}
        </footer>
      )}
    </section>
  )
}

/**
 * A setting whose control sits below its label: a text field or a dropdown. What it is goes under
 * the label; anything that qualifies it (a limit, a format) goes under the control.
 */
export const FieldRow: React.FC<{
  label: string
  description?: string
  note?: React.ReactNode
  tour?: string
  children: React.ReactNode
}> = ({ label, description, note, tour, children }) => (
  <div className="flex flex-col gap-1.5 mx-4 py-3 text-[13px]" data-tour={tour} role="group" aria-label={label}>
    <div>
      <div className="font-medium text-body">{label}</div>
      {description && <p className="mt-0.5 text-muted">{description}</p>}
    </div>
    {children}
    {note && <div className="text-muted">{note}</div>}
  </div>
)

/** A setting whose control sits beside its label: a switch or a button. */
export const InlineRow: React.FC<{
  label: string
  /** What it is; it can hold a link or a popover's trigger. */
  description?: React.ReactNode
  note?: React.ReactNode
  tour?: string
  children: React.ReactNode
}> = ({ label, description, note, tour, children }) => (
  <div className="flex items-center gap-6 mx-4 py-3 text-[13px]" data-tour={tour} role="group" aria-label={label}>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-body">{label}</div>
      {description && <p className="mt-0.5 text-muted">{description}</p>}
      {note && <div className="mt-0.5 text-muted">{note}</div>}
    </div>
    <div className="flex items-center gap-2 shrink-0">{children}</div>
  </div>
)

/** A secondary action written as an underlined link, under a row's label or control. */
export const TextLink: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <Button
    internalType="transparent"
    className="h-auto p-0 font-normal text-muted underline underline-offset-2 hover:bg-transparent hover:text-body"
    onClick={onClick}
  >
    {children}
  </Button>
)

/** A switch that can't be used right now, saying why on hover. */
export const WhyDisabled: React.FC<{ reason?: string; children: React.ReactElement }> = ({ reason, children }) =>
  reason ? (
    <Tooltip content={reason}>
      <span className="inline-flex">{children}</span>
    </Tooltip>
  ) : (
    children
  )

export interface Confirmation {
  title: string
  message: string
  /** Said in red under the message: a consequence the person confirming could be caught by. */
  warning?: string
  confirmLabel: string
  onConfirm: () => void
}

/** Asks before a setting with a wide or lasting effect takes effect. */
export const ConfirmDialog: React.FC<{ confirmation: Confirmation | null; onClose: () => void }> = ({
  confirmation,
  onClose
}) =>
  confirmation && (
    <Modal id="confirm-setting" open setOpen={(open) => !open && onClose()} width="small" title={confirmation.title}>
      <p className="text-[13px] text-muted">{confirmation.message}</p>
      {confirmation.warning && <p className="mt-3 text-[13px] text-red">{confirmation.warning}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <Button internalType="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          internalType="outline"
          className={BLUE_BUTTON}
          onClick={() => {
            confirmation.onConfirm()
            onClose()
          }}
        >
          {confirmation.confirmLabel}
        </Button>
      </div>
    </Modal>
  )

/** Whether focus is in a field's row, kept while it moves between the field and its buttons. */
export const useFocusWithin = (): [boolean, React.HTMLAttributes<HTMLDivElement>, () => void] => {
  const [focused, setFocused] = useState(false)
  return [
    focused,
    {
      onFocus: () => setFocused(true),
      onBlur: (event: React.FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false)
      }
    },
    () => setFocused(false)
  ]
}
